import * as React from 'react';

const Counter = function(props) {
  const {
          amount,
          animation,
          postfix = '',
          prefix  = '',
        } = props;

  const targetRef     = React.useRef(null);
  const animationRef  = React.useRef(null);
  const startTimeRef  = React.useRef(null);
  // Plain ref, not state: it's only ever read inside callbacks as a guard, never rendered.
  // Making it state used to be the bug — see the comment on startAnimation below.
  const hasAnimatedRef = React.useRef(false);

  const [count, setCount] = React.useState(0);

  const displayValue = React.useMemo(() => {
    if (typeof amount === 'undefined') {
      return '';
    }

    return new Intl.NumberFormat('ru-RU').format(animation ? count : amount);
  }, [amount, animation, count]);

  const animate        = React.useCallback((timestamp) => {
    if (!startTimeRef.current) {
      startTimeRef.current = timestamp;
    }

    // Was `setHasAnimated(true)` (state). That re-render gave `startAnimation` a new
    // identity (it had `hasAnimated` in its own deps), which re-ran the mount effect
    // below (it depends on `startAnimation`) — whose cleanup calls cancelAnimationFrame
    // on the very frame this callback had just scheduled a few lines down, freezing the
    // counter at whatever it showed on frame 1 (always 0, since elapsed is 0 there).
    // A ref sidesteps that: reading/writing it doesn't schedule a render, so nothing
    // downstream ever gets recreated mid-animation and the requestAnimationFrame chain
    // keeps running to completion.
    hasAnimatedRef.current = true;

    const elapsed  = timestamp - startTimeRef.current;
    const progress = Math.min(elapsed / 1000, 1);

    setCount(Math.floor(progress * (amount || 0)));

    if (progress < 1) {
      animationRef.current = requestAnimationFrame(animate);
    }
  }, [amount]);
  const startAnimation = React.useCallback(() => {
    if (hasAnimatedRef.current || typeof amount === 'undefined') {
      return;
    }

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    const rect = targetRef.current?.getBoundingClientRect();

    if (!rect) {
      return;
    }

    const isVisible = rect.top < window.innerHeight && rect.bottom >= 0;

    if (isVisible) {
      setCount(0);
      startTimeRef.current = null;
      animationRef.current = requestAnimationFrame(animate);
    }
  }, [amount, animate]);

  React.useEffect(() => {
    if (typeof amount !== 'undefined') {
      startAnimation();
      window.addEventListener('scroll', startAnimation);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      window.removeEventListener('scroll', startAnimation);
    };
  }, [amount, startAnimation]);

  return (
    <p
      className={'count'}
      ref={targetRef}
    >
      {prefix}
      {prefix && (!!displayValue || !!postfix) ? ' ' : ''}
      {displayValue}{!!postfix && !!displayValue ? ' ' : ''}
      {postfix}
    </p>
  );
};

export default Counter;